import { isValidSm3Hex, SM3 } from './sm3';
import type { HashWorkerRequest, HashWorkerResponse } from './hash.worker';

/** 摘要统一校验：结果不是合法的 SM3 十六进制串时直接抛错，避免异常结果被静默采用 */
function assertValidHash(hash: string): string {
  if (!isValidSm3Hex(hash)) {
    throw new Error(`哈希结果异常，已中止：${String(hash).slice(0, 40)}`);
  }
  return hash;
}

const CHUNK_SIZE = 8 * 1024 * 1024; // 8MB，兼顾内存占用与进度粒度

/**
 * yield = 机制 2：分片读取 + 主线程时间切片（默认）
 *   只依赖 File.slice / FileReader / Promise，兼容到 IE 10+，
 *   适合政务、机构等低版本浏览器环境。每算完一片主动让出主线程，
 *   因此页面可响应、进度可持续更新。
 * worker = 机制 1：分片 + Web Worker，主线程零阻塞，但依赖 Worker 环境。
 */
export type CalcMode = 'yield' | 'worker';

export interface CalcHashOptions {
  onProgress?: (percent: number) => void;
  mode?: CalcMode;
}

function blobToUint8Array(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  // 老浏览器兜底（Safari 14 以下没有 Blob.prototype.arrayBuffer）
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error || new Error('文件读取失败'));
    reader.readAsArrayBuffer(blob);
  });
}

/** 让出主线程：优先 MessageChannel（无 setTimeout 的 ~4ms 延迟），老浏览器回退 */
function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof MessageChannel === 'function') {
      const { port1, port2 } = new MessageChannel();
      port1.onmessage = () => {
        port1.close();
        resolve();
      };
      port2.postMessage(null);
    } else {
      setTimeout(resolve, 0);
    }
  });
}

/**
 * 机制 2：分片读取 + 主线程时间切片
 * 内存占用恒定为单片大小，可处理 1GB 级文件，且全程可上报进度。
 *
 * ⚠️ 严禁改为并发读取：SM3 是顺序敏感的链式结构，
 * 必须严格按字节偏移从小到大依次 update。若用 Promise.all 并发读分片、
 * 再按「完成顺序」喂给 update，结果必然是错误的哈希且不会报错。
 */
export async function calcFileHashByYield(file: Blob, onProgress?: (percent: number) => void): Promise<string> {
  const sm3 = new SM3();
  let loaded = 0;
  let lastReport = -1;

  while (loaded < file.size) {
    const end = Math.min(loaded + CHUNK_SIZE, file.size);
    sm3.update(await blobToUint8Array(file.slice(loaded, end)));
    loaded = end;

    const percent = Math.floor((loaded / file.size) * 100);
    if (percent !== lastReport) {
      lastReport = percent;
      onProgress?.(percent);
    }

    // 让出主线程，让渲染与交互有机会插入
    await yieldToBrowser();
  }

  // 保险 1：确认累计字节数与文件一致，杜绝任何情况下的静默截断
  if (loaded !== file.size) {
    throw new Error(`文件读取不完整：期望 ${file.size} 字节，实际读取 ${loaded} 字节`);
  }

  // 保险 2：确认摘要格式合法
  return assertValidHash(sm3.digest());
}

/** 机制 1：分片 + Web Worker，计算全程不在主线程 */
export function calcFileHashByWorker(file: Blob, onProgress?: (percent: number) => void): Promise<string> {
  if (typeof Worker === 'undefined' || typeof URL === 'undefined') {
    return calcFileHashByYield(file, onProgress);
  }

  return new Promise<string>((resolve, reject) => {
    let worker: Worker;

    try {
      worker = new Worker(new URL('./hash.worker.ts', import.meta.url));
    } catch {
      calcFileHashByYield(file, onProgress).then(resolve, reject);
      return;
    }

    worker.onmessage = (event: MessageEvent<HashWorkerResponse>) => {
      const { data } = event;
      if (data.type === 'progress') {
        onProgress?.(Math.floor((data.loaded / data.total) * 100));
      } else if (data.type === 'done') {
        worker.terminate();
        resolve(data.hash);
      } else {
        worker.terminate();
        reject(new Error(data.message));
      }
    };

    worker.onerror = (event) => {
      worker.terminate();
      reject(new Error(event.message || '哈希计算失败'));
    };

    const payload: HashWorkerRequest = { file, chunkSize: CHUNK_SIZE };
    worker.postMessage(payload);
  });
}

/**
 * 计算文件 SM3 哈希，默认使用机制 2（兼容性最好）。
 * 两种模式对同一文件产出完全相同的哈希。
 */
export function calcFileHash(file: Blob, options: CalcHashOptions = {}): Promise<string> {
  const { onProgress, mode = 'yield' } = options;
  if (mode === 'worker') return calcFileHashByWorker(file, onProgress);
  return calcFileHashByYield(file, onProgress);
}
